/**
 * The one URL every "get the app" QR code encodes.
 *
 * bowerlabs.ai/app is served by the public website (bower-labs/website,
 * `worker/app-install.ts`), not by this app: iPhones and iPads are redirected
 * to the App Store, Android phones with Google Play to Play, and everything
 * else (desktops, Android without Play, link previews) gets a page with both
 * store badges and a link to the web app. It needs no sign-in, which is why the
 * same URL can go on business cards and slides, and why its destination can
 * change without reprinting anything or shipping this app.
 *
 * `src` names the placement. The website records it on each scan (PostHog
 * `App QR Scanned`) and passes it to Google Play as the install referrer's
 * `utm_campaign`, so scans and installs can be compared per placement.
 */
export const APP_INSTALL_URL = 'https://bowerlabs.ai/app';

/**
 * The smart glasses guide on the public website (bower-labs/website,
 * `src/components/glasses-guide/`): which glasses work with Bower and how to
 * pair them. Linked from the Live sessions collection's splash.
 */
export const GLASSES_GUIDE_URL = 'https://bowerlabs.ai/glasses';

/**
 * The store listings bowerlabs.ai/app redirects to. index.html (the iOS Smart
 * App Banner) and public/manifest.json (Chrome's related-app prompt) name them
 * too, as static files that cannot import this, so a test pins all three.
 *
 * @internal Test seam: AppInstallBanner.test pins it against index.html and manifest.json.
 */
export const APP_STORE_ID = '6799662299';
/** @internal Test seam: AppInstallBanner.test pins it against public/manifest.json. */
export const PLAY_STORE_PACKAGE = 'com.bower.labs.mobile';

export type AppInstallSrc =
  | 'create_modal'
  | 'stream_empty'
  | 'onboarding'
  | 'checklist'
  | 'checklist_live'
  | 'setup_modal'
  | 'live_modal'
  /** The static image in the seeded welcome note (backend/scripts/gen-seed-qr.cjs). */
  | 'welcome_note'
  /** The "Get the app" card on the phone's capture page (useAppInstallBanner). */
  | 'mobile_web_banner'
  /** "Run on your phone" on the protocol page, on desktop (RunOnPhoneButton). */
  | 'protocol_run'
  /** Add equipment's "Photograph it with your phone" step. */
  | 'equipment_qr';

export function buildAppInstallUrl(src: AppInstallSrc): string {
  return `${APP_INSTALL_URL}?src=${src}`;
}
